import type { IUserProfile } from "./user";

export interface ISigninData {
  email: string;
  password: string;
}

export interface ISigninResponse {
  user: IUserProfile;
  accessToken: string;
  refreshToken: string;
}

export interface ISignupData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface ISignupResponse {
  message: string;
}

export interface IForgotPasswordData {
  email: string;
}

export interface IResetPasswordData {
  token: string;
  password: string;
}

export interface IInviteAcceptDetails {
  email: string;
  organizationName: string;
  requiresSso: boolean;
}

export interface IAcceptInviteData {
  token: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

export interface ISamlLoginParams {
  email: string;
  // NOTE: External-client identifier ("extension" / "mobile"). Echoed
  // through the SAML RelayState so the callback handler knows whether
  // to redirect tokens to `callbackUrl` instead of the web magic-link.
  // Optional — web clients omit it.
  source?: string;
  callbackUrl?: string;
  // NOTE: NSAI-6117 — an external client's PKCE S256 challenge. With it the
  // callback redirects a one-time code instead of the token pair; the client
  // redeems it with the verifier through `AuthApi.exchangeAuthCode`.
  codeChallenge?: string;
}

export interface ISamlConfig {
  organizationUuid: string;
  organizationName: string;
  samlUrl: string;
}

export interface IAuthMessageResponse {
  message: string;
}

export interface IAuthRedirectResponse {
  url: string;
}

export interface IRefreshTokenResponse {
  accessToken: string;
  // NOTE: NSAI-6847 — the session's refresh credential, returned with every
  // refresh. NSAI-4194 — a rotated replacement: the one presented stops
  // working shortly after. Only the clients that keep it themselves (mobile,
  // the extension) store it; the web's stays in its httpOnly cookie.
  refreshToken?: string;
}

export interface IRegistrationStatusResponse {
  registrationEnabled: boolean;
  googleAuthEnabled: boolean;
  githubAuthEnabled: boolean;
  yandexAuthEnabled: boolean;
  vkAuthEnabled: boolean;
  telegramAuthEnabled: boolean;
  externalApiEnabled: boolean;
  // NOTE: NSAI-6117 — true from a server that answers an external client's
  // PKCE challenge with a one-time code. Absent from a server that predates
  // the hand-off, which still redirects the token pair itself.
  isAuthCodeExchangeSupported?: boolean;
}

export interface ISingleOrganizationSaml {
  samlUrl: string;
}

// NOTE: Optional query params accepted by GET /auth/saml/organization.
// External clients (browser extension, mobile) pass these so the SAML
// callback can redirect tokens to `callbackUrl` instead of the web
// magic-link. Web callers omit them.
export interface IGetSingleOrganizationSamlParams {
  source?: string;
  callbackUrl?: string;
  // NOTE: NSAI-6117 — see ISamlLoginParams.
  codeChallenge?: string;
}

// NOTE: NSAI-6117 — POST /auth/exchange-code: the one-time code an external
// client's sign-in redirect ended with, and the PKCE verifier it was bound to.
export interface IExchangeAuthCodeData {
  code: string;
  codeVerifier: string;
}

export interface IExchangeAuthCodeResponse {
  accessToken: string;
  refreshToken: string;
  // NOTE: NSAI-6755 — the SSO warning code that used to ride the redirect
  // fragment beside the tokens.
  ssoWarningCode?: string;
}

export enum ESocialAuthProvider {
  GITHUB = "github",
  GOOGLE = "google",
  YANDEX = "yandex",
  VK = "vk",
  TELEGRAM = "telegram",
}
